import React, { useState, useEffect, useCallback } from 'react';
import { realSignalApi, Alert as ApiAlert, ApiState } from '../../services/realsignal-api';

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    backgroundCard: '#1C2E42',
    backgroundWell: '#0A1218',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    accentDark: '#2E4A5A',
    border: '#1C2E42',
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#EF4444',
};

interface DisplayAlert {
    id: string;
    type: 'info' | 'warning' | 'critical';
    title: string;
    message: string;
    timestamp: string;
    read: boolean;
    propertyId?: string;
    severity: 'info' | 'warning' | 'critical';
}

function transformApiAlert(apiAlert: ApiAlert): DisplayAlert {
    return {
        id: apiAlert.id,
        type: apiAlert.severity,
        title: apiAlert.title,
        message: apiAlert.message,
        timestamp: formatTimestamp(apiAlert.createdAt),
        read: false,
        propertyId: apiAlert.propertyId,
        severity: apiAlert.severity,
    };
}

function formatTimestamp(isoString: string): string {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    return date.toLocaleDateString();
}

const getAlertIcon = (severity: DisplayAlert['severity']) => {
    switch (severity) {
        case 'critical':
            return (
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.error}
                    strokeWidth="2"
                >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
            );
        case 'warning':
            return (
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.warning}
                    strokeWidth="2"
                >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                </svg>
            );
        case 'info':
        default:
            return (
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.accent}
                    strokeWidth="2"
                >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" />
                </svg>
            );
    }
};

interface AlertsInboxProps {
    onBack: () => void;
}

export const AlertsInbox: React.FC<AlertsInboxProps> = ({ onBack }) => {
    const [alerts, setAlerts] = useState<DisplayAlert[]>([]);
    const [filter, setFilter] = useState<'all' | 'unread'>('all');
    const [apiState, setApiState] = useState<ApiState>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalAlerts, setTotalAlerts] = useState(0);
    const pageSize = 20;

    const fetchAlerts = useCallback(async () => {
        setApiState('loading');
        setErrorMessage(null);

        const result = await realSignalApi.getAlerts({
            tenant: 'harris',
            page,
            pageSize,
        });

        setApiState(result.state);

        if (result.state === 'success' && result.data) {
            const displayAlerts = result.data.alerts.map(transformApiAlert);
            setAlerts(displayAlerts);
            setTotalAlerts(result.data.total);
        } else if (result.state === 'empty' && result.data) {
            setAlerts([]);
            setTotalAlerts(0);
        } else if (result.error) {
            setErrorMessage(result.error);
        }
    }, [page]);

    useEffect(() => {
        fetchAlerts();
    }, [fetchAlerts]);

    const filteredAlerts = filter === 'unread' ? alerts.filter(a => !a.read) : alerts;
    const unreadCount = alerts.filter(a => !a.read).length;

    const markAsRead = (id: string) => {
        setAlerts(alerts.map(a => (a.id === id ? { ...a, read: true } : a)));
    };

    const markAllAsRead = () => {
        setAlerts(alerts.map(a => ({ ...a, read: true })));
    };

    const totalPages = Math.ceil(totalAlerts / pageSize);

    const renderContent = () => {
        if (apiState === 'loading') {
            return (
                <div
                    style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        backgroundColor: colors.backgroundCard,
                        borderRadius: '12px',
                        border: `1px solid ${colors.border}`,
                    }}
                >
                    <div
                        style={{
                            width: '40px',
                            height: '40px',
                            border: `3px solid ${colors.border}`,
                            borderTopColor: colors.accent,
                            borderRadius: '50%',
                            margin: '0 auto 16px',
                            animation: 'spin 1s linear infinite',
                        }}
                    />
                    <p style={{ fontSize: '14px', color: colors.textMuted, margin: 0 }}>
                        Loading alerts...
                    </p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            );
        }

        if (apiState === 'authError') {
            return (
                <div
                    style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        backgroundColor: colors.backgroundCard,
                        borderRadius: '12px',
                        border: `1px solid ${colors.error}`,
                    }}
                >
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={colors.error}
                        strokeWidth="1.5"
                        style={{ marginBottom: '16px' }}
                    >
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <h3
                        style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            margin: '0 0 8px 0',
                            color: colors.textPrimary,
                        }}
                    >
                        Authentication Required
                    </h3>
                    <p style={{ fontSize: '14px', color: colors.textMuted, margin: 0 }}>
                        Please sign in to view your alerts
                    </p>
                </div>
            );
        }

        if (apiState === 'error' || apiState === 'notDeployed') {
            return (
                <div
                    style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        backgroundColor: colors.backgroundCard,
                        borderRadius: '12px',
                        border: `1px solid ${colors.error}`,
                    }}
                >
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={colors.error}
                        strokeWidth="1.5"
                        style={{ marginBottom: '16px' }}
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <h3
                        style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            margin: '0 0 8px 0',
                            color: colors.textPrimary,
                        }}
                    >
                        {apiState === 'notDeployed'
                            ? 'Service Unavailable'
                            : 'Failed to Load Alerts'}
                    </h3>
                    <p style={{ fontSize: '14px', color: colors.textMuted, margin: '0 0 16px 0' }}>
                        {errorMessage || 'An error occurred while fetching alerts'}
                    </p>
                    <button
                        onClick={fetchAlerts}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: colors.accent,
                            border: 'none',
                            borderRadius: '8px',
                            color: colors.textPrimary,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                        }}
                    >
                        Try Again
                    </button>
                </div>
            );
        }

        if (filteredAlerts.length === 0) {
            return (
                <div
                    style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        backgroundColor: colors.backgroundCard,
                        borderRadius: '12px',
                        border: `1px solid ${colors.border}`,
                    }}
                >
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={colors.textMuted}
                        strokeWidth="1.5"
                        style={{ marginBottom: '16px' }}
                    >
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                    <h3
                        style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            margin: '0 0 8px 0',
                            color: colors.textPrimary,
                        }}
                    >
                        {filter === 'unread' ? 'No unread alerts' : 'No alerts yet'}
                    </h3>
                    <p style={{ fontSize: '14px', color: colors.textMuted, margin: 0 }}>
                        {filter === 'unread'
                            ? "You're all caught up!"
                            : 'Enable alerts on your saved searches to get notified of new matches'}
                    </p>
                </div>
            );
        }

        return (
            <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {filteredAlerts.map(alert => (
                        <div
                            key={alert.id}
                            onClick={() => markAsRead(alert.id)}
                            style={{
                                display: 'flex',
                                gap: '16px',
                                padding: '16px',
                                backgroundColor: alert.read
                                    ? colors.backgroundCard
                                    : colors.backgroundLight,
                                borderRadius: '12px',
                                border: `1px solid ${alert.read ? colors.border : colors.accent}`,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                            }}
                        >
                            <div
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    backgroundColor: colors.backgroundWell,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                {getAlertIcon(alert.severity)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '4px',
                                    }}
                                >
                                    <h3
                                        style={{
                                            fontSize: '15px',
                                            fontWeight: alert.read ? 500 : 600,
                                            margin: 0,
                                            color: colors.textPrimary,
                                        }}
                                    >
                                        {alert.title}
                                    </h3>
                                    <span
                                        style={{
                                            fontSize: '12px',
                                            color: colors.textMuted,
                                            flexShrink: 0,
                                            marginLeft: '12px',
                                        }}
                                    >
                                        {alert.timestamp}
                                    </span>
                                </div>
                                <p
                                    style={{
                                        fontSize: '14px',
                                        color: colors.textSecondary,
                                        margin: '0 0 8px 0',
                                    }}
                                >
                                    {alert.message}
                                </p>
                                {alert.propertyId && (
                                    <div
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '6px 10px',
                                            backgroundColor: colors.backgroundWell,
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            color: colors.textSecondary,
                                        }}
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                        Property: {alert.propertyId}
                                    </div>
                                )}
                            </div>
                            {!alert.read && (
                                <div
                                    style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: colors.accent,
                                        flexShrink: 0,
                                        marginTop: '6px',
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
                {totalPages > 1 && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '16px',
                            marginTop: '24px',
                        }}
                    >
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            style={{
                                padding: '8px 16px',
                                backgroundColor:
                                    page === 1 ? colors.backgroundWell : colors.backgroundCard,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '8px',
                                color: page === 1 ? colors.textMuted : colors.textSecondary,
                                cursor: page === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                            }}
                        >
                            Previous
                        </button>
                        <span style={{ fontSize: '14px', color: colors.textSecondary }}>
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            style={{
                                padding: '8px 16px',
                                backgroundColor:
                                    page === totalPages
                                        ? colors.backgroundWell
                                        : colors.backgroundCard,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '8px',
                                color:
                                    page === totalPages ? colors.textMuted : colors.textSecondary,
                                cursor: page === totalPages ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                            }}
                        >
                            Next
                        </button>
                    </div>
                )}
            </>
        );
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                backgroundColor: colors.background,
                color: colors.textPrimary,
            }}
        >
            <nav
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 24px',
                    backgroundColor: colors.backgroundLight,
                    borderBottom: `1px solid ${colors.border}`,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                        onClick={onBack}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: 'transparent',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '8px',
                            color: colors.textSecondary,
                            cursor: 'pointer',
                            fontSize: '14px',
                        }}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Back to Dashboard
                    </button>
                    <h1 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>
                        Alerts
                        {unreadCount > 0 && (
                            <span
                                style={{
                                    marginLeft: '8px',
                                    padding: '2px 8px',
                                    backgroundColor: colors.accent,
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                }}
                            >
                                {unreadCount}
                            </span>
                        )}
                    </h1>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                        style={{
                            display: 'flex',
                            backgroundColor: colors.backgroundWell,
                            borderRadius: '8px',
                            padding: '4px',
                        }}
                    >
                        <button
                            onClick={() => setFilter('all')}
                            style={{
                                padding: '6px 16px',
                                backgroundColor:
                                    filter === 'all' ? colors.backgroundCard : 'transparent',
                                border: 'none',
                                borderRadius: '6px',
                                color: filter === 'all' ? colors.textPrimary : colors.textMuted,
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 500,
                            }}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilter('unread')}
                            style={{
                                padding: '6px 16px',
                                backgroundColor:
                                    filter === 'unread' ? colors.backgroundCard : 'transparent',
                                border: 'none',
                                borderRadius: '6px',
                                color: filter === 'unread' ? colors.textPrimary : colors.textMuted,
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 500,
                            }}
                        >
                            Unread
                        </button>
                    </div>
                    {unreadCount > 0 && (
                        <button
                            onClick={markAllAsRead}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: 'transparent',
                                border: `1px solid ${colors.border}`,
                                borderRadius: '8px',
                                color: colors.textSecondary,
                                cursor: 'pointer',
                                fontSize: '13px',
                            }}
                        >
                            Mark all as read
                        </button>
                    )}
                </div>
            </nav>

            <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                {renderContent()}
            </div>
        </div>
    );
};
